import type { Permission } from '@artiman/shared';
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth';

declare module 'vue-router' {
  interface RouteMeta {
    title?: string;
    /** All of these permissions are required. */
    permission?: Permission | Permission[];
    public?: boolean;
    /** Sidebar item to highlight for routes that are not in the sidebar. */
    section?: string;
  }
}

/** Sidebar items (in order). Items the admin lacks permission for are hidden and their routes refused. */
export const NAV: Array<{ name: string; path: string; title: string; icon: string; permission?: Permission | Permission[] }> = [
  { name: 'dashboard', path: '/', title: 'داشبورد', icon: 'M4 11l8-6 8 6v8a1 1 0 0 1-1 1h-4v-6H9v6H5a1 1 0 0 1-1-1z' },
  { name: 'prices', path: '/prices', title: 'قیمت‌ها و منابع', icon: 'M4 19V9M9 19V5M14 19v-7M19 19V8', permission: 'prices.read' },
  { name: 'users', path: '/users', title: 'کاربران', icon: 'M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM2 21c1-4 4-6 7-6s6 2 7 6M17 11a3 3 0 1 0 0-6M19 21c-.4-2.5-1.6-4.2-3-5', permission: 'users.read' },
  { name: 'content', path: '/content', title: 'محتوای سایت', icon: 'M4 5h16M4 10h16M4 15h10M4 20h7', permission: 'content.manage' },
  { name: 'posts', path: '/posts', title: 'بلاگ', icon: 'M7 4h7l4 4v12H7zM14 4v4h4M9 12h6M9 16h5', permission: 'content.manage' },
  { name: 'newsfeed', path: '/newsfeed', title: 'اخبار و تقویم', icon: 'M5 5h14v15H5zM5 9h14M9 3v4M15 3v4M8 13h3M8 16h6', permission: 'content.manage' },
  { name: 'newsletter', path: '/newsletter', title: 'خبرنامه', icon: 'M4 6h16v12H4zM4 7l8 6 8-6', permission: 'content.manage' },
  { name: 'academy', path: '/academy', title: 'آکادمی', icon: 'M3 9l9-5 9 5-9 5zM7 11.5V16c0 1.5 2.5 3 5 3s5-1.5 5-3v-4.5M21 9v6', permission: 'academy.manage' },
  { name: 'signals', path: '/signals', title: 'سیگنال‌ها و تحلیل', icon: 'M4 18l5-6 4 3 7-9M15 6h5v5', permission: 'analysis.manage' },
  { name: 'advisor', path: '/advisor', title: 'دستیار و سبدها', icon: 'M12 3a9 9 0 1 0 9 9h-9zM14 3.3A9 9 0 0 1 20.7 10H14z', permission: 'analysis.manage' },
  { name: 'gamification', path: '/gamification', title: 'امتیاز و جوایز', icon: 'M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0zM7 6H4a3 3 0 0 0 3 4M17 6h3a3 3 0 0 1-3 4', permission: 'gamification.manage' },
  { name: 'credit', path: '/credit', title: 'تسهیلات', icon: 'M3 7h18v10H3zM3 11h18M7 15h3', permission: 'credit.manage' },
  { name: 'finance', path: '/finance', title: 'مالی و اشتراک', icon: 'M4 7h14a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2zM4 7l11-3 1 3M16 13h2', permission: 'finance.manage' },
  { name: 'admins', path: '/admins', title: 'مدیران و نقش‌ها', icon: 'M12 3l7 3v5c0 5-3.5 8.5-7 10-3.5-1.5-7-5-7-10V6z', permission: 'admins.manage' },
  { name: 'audit', path: '/audit', title: 'لاگ ممیزی', icon: 'M7 4h7l4 4v12H7zM14 4v4h4M9 12h6M9 16h5', permission: 'audit.read' },
  { name: 'account', path: '/account', title: 'حساب من', icon: 'M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM5 20c1.5-3.5 4-5 7-5s5.5 1.5 7 5' },
];

const views: Record<string, () => Promise<unknown>> = {
  dashboard: () => import('@/views/DashboardView.vue'),
  prices: () => import('@/views/PricesView.vue'),
  users: () => import('@/views/UsersView.vue'),
  content: () => import('@/views/ContentView.vue'),
  posts: () => import('@/views/PostsView.vue'),
  newsfeed: () => import('@/views/NewsFeedView.vue'),
  newsletter: () => import('@/views/NewsletterView.vue'),
  academy: () => import('@/views/AcademyView.vue'),
  signals: () => import('@/views/SignalsView.vue'),
  advisor: () => import('@/views/AdvisorView.vue'),
  gamification: () => import('@/views/GamificationView.vue'),
  credit: () => import('@/views/CreditView.vue'),
  finance: () => import('@/views/FinanceView.vue'),
  admins: () => import('@/views/AdminsView.vue'),
  audit: () => import('@/views/AuditView.vue'),
  account: () => import('@/views/AccountView.vue'),
};

const routes: RouteRecordRaw[] = [
  { path: '/login', name: 'login', component: () => import('@/views/LoginView.vue'), meta: { public: true, title: 'ورود' } },
  {
    path: '/',
    component: () => import('@/layouts/AppShell.vue'),
    children: [
      ...NAV.map(n => ({
        path: n.path === '/' ? '' : n.path.slice(1),
        name: n.name,
        component: views[n.name]!,
        meta: { title: n.title, permission: n.permission },
      })),
      // Article editor (`new` or a post id); highlighted as «بلاگ» in the sidebar.
      {
        path: 'posts/:id', name: 'post-editor', component: () => import('@/views/PostEditorView.vue'),
        meta: { title: 'ویرایش مطلب', permission: 'content.manage' as const, section: 'posts' },
      },
      // Course editor (`new` or a course id); highlighted as «آکادمی».
      {
        path: 'academy/:id', name: 'course-editor', component: () => import('@/views/CourseEditorView.vue'),
        meta: { title: 'ویرایش دوره', permission: 'academy.manage' as const, section: 'academy' },
      },
    ],
  },
  { path: '/:pathMatch(.*)*', redirect: '/' },
];

export const router = createRouter({ history: createWebHistory(), routes });

router.beforeEach(async to => {
  const auth = useAuthStore();
  await auth.init();
  if (to.meta.public) return auth.admin && to.name === 'login' ? { name: 'dashboard' } : true;
  if (!auth.admin) return { name: 'login', query: { redirect: to.fullPath } };
  if (!auth.can(to.meta.permission)) return { name: 'dashboard' };
  return true;
});

router.afterEach(to => {
  document.title = to.meta.title ? `${to.meta.title} | پنل آرتیمان` : 'پنل مدیریت آرتیمان';
});
